import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, forkJoin, of, switchMap, take, throwError, Observable } from 'rxjs';
import { ToastService } from '../../ui';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { SessionService } from '../../core/session/session.service';
import { AuthService } from '../../core/auth/auth.service';
import { CommunicationService } from './communication.service';
import { LoggingService } from '../../core/services/logging.service';
import {
  EmailAddress,
  FaxBookingRequest,
  FaxNumber,
  NotificationData,
} from './communication.model';
import { ReachabilityRef } from '../../core/models/reachability-ref.model';
import {
  AddReachabilityDialogComponent,
  AddReachabilityDialogResult,
} from '../notifications-forwardings/add-reachability-dialog.component';
import { ReachabilitiesService } from '../reachabilities/reachabilities.service';
import { Reachability, ReachabilityType, REACHABILITY_NO_ID_ERROR } from '../reachabilities/reachabilities.model';
import { AddEmailDialogComponent, AddEmailDialogResult } from './add-email-dialog.component';
import { BookNumberDialogComponent, BookNumberDialogResult } from '../cc-numbers/book-number-dialog.component';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { TypeFilterComponent } from '../../shared/type-filter/type-filter.component';
import { CappedListComponent } from '../../shared/capped-list/capped-list.component';
import { RevealOnExpandDirective } from '../../shared/directives/reveal-on-expand.directive';
import { TruncateTokenPipe, isTruncated } from '../../shared/pipes/truncate-token.pipe';
import { typeBadgeVariant } from '../../shared/utils/reachability-type-badge';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import { SNACKBAR_ERROR_CONFIG } from '../../shared/utils/notification-defaults';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
  HlmTooltipDirective,
} from '../../ui';

/**
 * Maps the shared booking dialog's result (callcenter-shaped: `desired*` fields, number `type`)
 * onto the fax booking input — the number type does not apply to fax and is dropped.
 * @ticket EB-3809
 */
export function toFaxBookingRequest(result: BookNumberDialogResult): FaxBookingRequest {
  const data = result.phoneNumberData;
  return {
    countryPrefix: data.desiredCountryPrefix,
    cityPrefix: data.desiredCityPrefix,
    cityName: data.desiredCityName,
    regulationInformation: data.regulationInformation ?? null,
    addressProofFile: result.addressProofFile,
    identityProofFile: result.identityProofFile,
  };
}

@Component({
  selector: 'cmt-communication',
  imports: [
    ErrorStateComponent,
    HlmBadgeComponent,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmSpinnerComponent,
    HlmTooltipDirective,
    TruncateTokenPipe,
    TypeFilterComponent,
    CappedListComponent,
    RevealOnExpandDirective,
  ],
  templateUrl: './communication.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommunicationComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(CommunicationService);
  private readonly reachApi = inject(ReachabilitiesService);
  readonly session = inject(SessionService);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly loggingService = inject(LoggingService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private userAccountId = 0;

  // --- State ---
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  // Emails
  readonly emails = signal<EmailAddress[]>([]);
  readonly domains = signal<string[]>([]);
  readonly emailSaving = signal(false);

  // Per-email notifications: keyed by "username@domain"
  readonly emailNotifications = signal<Record<string, NotificationData[]>>({});
  readonly emailAddableReachabilities = signal<Record<string, ReachabilityRef[]>>({});
  readonly emailNotifLoading = signal<Record<string, boolean>>({});

  // Fax
  readonly faxNumbers = signal<FaxNumber[]>([]);
  /** Lock for the user account in view — true while a booking for THAT account is in flight. */
  readonly faxSaving = signal(false);
  /**
   * User accounts with a booking POST in flight. A plain flag reset on navigation let A → B → A
   * unlock Book on A mid-request — a second billable booking (EB-3809 review run 3).
   */
  private readonly faxBookingsInFlight = new Set<number>();

  // Per-fax notifications: keyed by full number string
  readonly faxNotifications = signal<Record<string, NotificationData[]>>({});
  readonly faxAddableReachabilities = signal<Record<string, ReachabilityRef[]>>({});
  readonly faxNotifLoading = signal<Record<string, boolean>>({});

  // (Add/edit email + add notification flows are dialog-based — no inline form state needed)

  // --- Filter + collapse (mirrors the Reachabilities page) ---
  readonly filterText = signal('');
  readonly collapsed = signal<Record<string, boolean>>({});
  private readonly sectionKeys = ['emails', 'fax'];

  readonly allCollapsed = computed(() =>
    this.sectionKeys.every(k => this.collapsed()[k] ?? false),
  );

  /** Emails matching the filter — by address or any of their notifications. */
  readonly filteredEmails = computed<EmailAddress[]>(() => {
    const q = this.filterText().toLowerCase().trim();
    if (!q) return this.emails();
    return this.emails().filter(e =>
      this.emailKey(e).toLowerCase().includes(q) ||
      this.notifMatches(this.getEmailNotifs(e), q),
    );
  });

  /** Fax numbers matching the filter — by number or any of their notifications. */
  readonly filteredFaxNumbers = computed<FaxNumber[]>(() => {
    const q = this.filterText().toLowerCase().trim();
    if (!q) return this.faxNumbers();
    return this.faxNumbers().filter(f =>
      this.faxDisplayNumber(f).toLowerCase().includes(q) ||
      this.faxKey(f).toLowerCase().includes(q) ||
      this.notifMatches(this.getFaxNotifs(f), q),
    );
  });

  readonly hiddenSectionCount = computed(() => {
    if (!this.filterText()) return 0;
    let hidden = 0;
    if (this.emails().length > 0 && this.filteredEmails().length === 0) hidden++;
    if (this.faxNumbers().length > 0 && this.filteredFaxNumbers().length === 0) hidden++;
    return hidden;
  });

  private notifMatches(notifs: NotificationData[], q: string): boolean {
    return notifs.some(n =>
      n.reachability.name.toLowerCase().includes(q) ||
      n.reachability.destination.toLowerCase().includes(q) ||
      n.reachability.type.toLowerCase().includes(q),
    );
  }

  toggleSection(key: string): void {
    this.collapsed.update(c => ({ ...c, [key]: !(c[key] ?? false) }));
  }

  isCollapsed(key: string): boolean {
    return this.collapsed()[key] ?? false;
  }

  toggleAll(): void {
    const shouldCollapse = !this.allCollapsed();
    const next: Record<string, boolean> = {};
    for (const k of this.sectionKeys) next[k] = shouldCollapse;
    this.collapsed.set(next);
  }

  /** Colored Type badge + token truncation — shared with the Reachabilities page. */
  readonly typeBadgeVariant = typeBadgeVariant;
  readonly isTokenTruncated = isTruncated;

  copyToClipboard(text: string, event?: Event): void {
    copyToClipboard(this.toast, text, event);
  }

  // --- Type quick-filter — PUSH deselected by default (it's the noisy one) ---
  readonly selectedTypes = signal<string[] | null>(null);

  readonly availableTypes = computed<string[]>(() => {
    const seen = new Set<string>();
    Object.values(this.emailNotifications()).forEach(list => list.forEach(n => seen.add(n.reachability.type)));
    Object.values(this.faxNotifications()).forEach(list => list.forEach(n => seen.add(n.reachability.type)));
    return [...seen];
  });

  readonly effectiveSelectedTypes = computed<string[]>(() =>
    this.selectedTypes() ?? this.availableTypes().filter(t => t !== 'PUSH'),
  );

  private typeVisible(type: string): boolean {
    return this.effectiveSelectedTypes().includes(type);
  }

  /** Per-email notifications passing the type quick-filter. */
  visibleEmailNotifs(email: EmailAddress): NotificationData[] {
    return this.getEmailNotifs(email).filter(n => this.typeVisible(n.reachability.type));
  }

  /** Per-fax notifications passing the type quick-filter. */
  visibleFaxNotifs(fax: FaxNumber): NotificationData[] {
    return this.getFaxNotifs(fax).filter(n => this.typeVisible(n.reachability.type));
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const uaId = Number(params['userAccountId']);
      if (uaId) {
        this.userAccountId = uaId;
        // The lock follows the account: one booking in flight elsewhere does not lock this one.
        this.faxSaving.set(this.faxBookingsInFlight.has(uaId));
        this.loadAll();
      }
    });
  }

  loadAll(): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      emails: this.api.getEmails(this.userAccountId),
      domains: this.api.getDomains(),
      faxNumbers: this.api.getFaxNumbers(this.userAccountId),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.emails.set(data.emails);
        this.domains.set(data.domains);
        this.faxNumbers.set(data.faxNumbers);
        this.loading.set(false);
        // Eagerly load notifications inline (no more expand-to-reveal)
        data.emails.forEach(e => this.loadEmailNotifications(e));
        data.faxNumbers.forEach(f => this.loadFaxNotifications(f));
      },
      error: (err) => {
        this.error.set(extractErrorMessage(err, 'Failed to load communication data'));
        this.loading.set(false);
      },
    });
  }

  // --- Email helpers ---

  emailKey(email: EmailAddress): string {
    return `${email.username}@${email.domain}`;
  }

  faxKey(fax: FaxNumber): string {
    return `${fax.diallingCode}${fax.prefix}${fax.suffix}`;
  }

  // --- Email CRUD (dialog-based) ---

  /** Open the Add Email dialog — twin of the Add Notification flow. */
  openAddEmailDialog(): void {
    const ref = this.dialog.open(AddEmailDialogComponent, {
      data: {
        domains: this.domains(),
        mode: 'add',
      },
      disableClose: true,
    });
    ref.afterClosed().subscribe((result: AddEmailDialogResult | undefined) => {
      if (!result) return;
      this.persistEmail(null, result);
    });
  }

  /** Open the Edit Email dialog (same shell, pre-filled with current values). */
  openEditEmailDialog(email: EmailAddress): void {
    const ref = this.dialog.open(AddEmailDialogComponent, {
      data: {
        domains: this.domains(),
        initialUsername: email.username,
        initialDomain: email.domain,
        mode: 'edit',
      },
      disableClose: true,
    });
    ref.afterClosed().subscribe((result: AddEmailDialogResult | undefined) => {
      if (!result) return;
      this.persistEmail(email, result);
    });
  }

  /**
   * Single persist path for both add + edit — keeps post-save side effects
   * (snackbar, BE-seeded notifications, customer reload) in one place.
   */
  private persistEmail(editing: EmailAddress | null, val: AddEmailDialogResult): void {
    if (this.emailSaving()) return;
    this.emailSaving.set(true);

    const payload = { username: val.username, domain: val.domain, userAccountId: this.userAccountId };
    const op$ = editing
      ? this.api.updateEmail(editing.domain, editing.username, payload)
      : this.api.createEmail(payload);

    op$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.emailSaving.set(false);

        const newKey = `${val.username}@${val.domain}`;
        const isCreate = !editing;

        // For new emails: BE seeds default notifications asynchronously — show
        // loading spinner immediately so the slot doesn't flash an empty state
        if (isCreate) {
          this.emailNotifLoading.update(m => ({ ...m, [newKey]: true }));
        }

        this.api.getEmails(this.userAccountId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(emails => {
            this.emails.set(emails);
            const target = emails.find(e => this.emailKey(e) === newKey);
            if (!target) return;
            if (isCreate) {
              this.loadEmailNotificationsWithRetry(target);
            } else {
              this.loadEmailNotifications(target);
            }
          });

        this.session.reloadCustomer();
        this.showEmailSenderHint();
        this.toast.open(editing ? 'Email updated' : 'Email created', 'OK', { duration: 3000 });
      },
      error: (err) => {
        this.emailSaving.set(false);
        this.toast.open(extractErrorMessage(err, 'Failed to save email'), 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }

  /**
   * After creating a new email, the BE asynchronously seeds default notifications.
   * Polls the notifications endpoint until either notifications appear or max retries hit.
   * Keeps the per-email loading flag true until done so UI shows a spinner.
   */
  private loadEmailNotificationsWithRetry(email: EmailAddress, retriesLeft = 5, delayMs = 500): void {
    const key = this.emailKey(email);
    this.emailNotifLoading.update(m => ({ ...m, [key]: true }));

    setTimeout(() => {
      forkJoin({
        notifications: this.api.getEmailNotifications(email.domain, email.username),
        addable: this.api.getEmailAddableReachabilities(email.domain, email.username),
      }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (data) => {
          if (data.notifications.length === 0 && retriesLeft > 0) {
            // BE may still be seeding — back off and retry
            this.loadEmailNotificationsWithRetry(email, retriesLeft - 1, delayMs);
            return;
          }
          this.emailNotifications.update(m => ({ ...m, [key]: data.notifications }));
          this.emailAddableReachabilities.update(m => ({ ...m, [key]: data.addable }));
          this.emailNotifLoading.update(m => ({ ...m, [key]: false }));
        },
        error: () => {
          this.emailNotifLoading.update(m => ({ ...m, [key]: false }));
        },
      });
    }, delayMs);
  }

  confirmDeleteEmail(email: EmailAddress): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Email',
        message: `Really delete email ${email.username}@${email.domain}?`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'delete',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.api.deleteEmail(email.domain, email.username, this.userAccountId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.emails.update(list => list.filter(e => this.emailKey(e) !== this.emailKey(email)));
            this.session.reloadCustomer();
            this.showEmailSenderHint();
            this.toast.open('Email deleted', 'OK', { duration: 3000 });
          },
          error: (err) => {
            this.toast.open(extractErrorMessage(err, 'Failed to delete email'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  private showEmailSenderHint(): void {
    this.toast.open(
      'Note: Please verify the Email Sender Address in User Account Information.',
      'Go to UA Info',
      { duration: 8000, tone: 'default' },
    );
  }

  // --- Email Notifications ---

  loadEmailNotifications(email: EmailAddress): void {
    const key = this.emailKey(email);
    this.emailNotifLoading.update(m => ({ ...m, [key]: true }));

    forkJoin({
      notifications: this.api.getEmailNotifications(email.domain, email.username),
      addable: this.api.getEmailAddableReachabilities(email.domain, email.username),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.emailNotifications.update(m => ({ ...m, [key]: data.notifications }));
        this.emailAddableReachabilities.update(m => ({ ...m, [key]: data.addable }));
        this.emailNotifLoading.update(m => ({ ...m, [key]: false }));
      },
      error: () => {
        this.emailNotifLoading.update(m => ({ ...m, [key]: false }));
      },
    });
  }

  getEmailNotifs(email: EmailAddress): NotificationData[] {
    return this.emailNotifications()[this.emailKey(email)] ?? [];
  }

  getEmailAddable(email: EmailAddress): ReachabilityRef[] {
    return this.emailAddableReachabilities()[this.emailKey(email)] ?? [];
  }

  isEmailNotifLoading(email: EmailAddress): boolean {
    return this.emailNotifLoading()[this.emailKey(email)] ?? false;
  }

  /**
   * Open the same dialog used by the Notifications & Forwardings page.
   * Creating a new reachability is allowed here too — we scope the create to
   * the first PS owned by this user account (mirrors how the BE associates
   * UA-level reachabilities).
   */
  openAddEmailNotifDialog(email: EmailAddress): void {
    const ref = this.dialog.open(AddReachabilityDialogComponent, {
      data: {
        addableList: this.getEmailAddable(email),
        sectionLabel: `Notification for ${email.username}@${email.domain}`,
        allowCreate: this.canCreateReachability(),
        allowedTypes: ['EMAIL'],
      },
      disableClose: true,
    });

    ref.afterClosed().subscribe((result: AddReachabilityDialogResult | null) => {
      if (!result) return;
      const attach$ = this.resolveReachabilityId(result).pipe(
        switchMap(reachId => this.api.addEmailNotification(email.domain, email.username, {
          reachability: { reachabilityId: reachId },
        })),
        takeUntilDestroyed(this.destroyRef),
      );
      attach$.subscribe({
        next: () => {
          this.loadEmailNotifications(email);
          this.toast.open(
            result.mode === 'create' ? 'Reachability created and notification added' : 'Notification added',
            'OK',
            { duration: 3000 },
          );
        },
        error: (err) => {
          this.toast.open(extractErrorMessage(err, 'Failed to add notification'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
    });
  }

  /**
   * Resolve the reachability id from the dialog result.
   * For 'existing' we already have it. For 'create' we POST a new reachability
   * scoped to the first PS belonging to this UA, carrying the user's type-
   * specific choices (publiclyAvailable, emailFormat for EMAIL).
   */
  private resolveReachabilityId(result: AddReachabilityDialogResult): Observable<number> {
    if (result.mode === 'existing') {
      return of(result.reachabilityId);
    }
    const psId = this.firstPhoneServiceIdForUa();
    return this.reachApi.create(psId, {
      name: result.name,
      destination: result.destination,
      type: result.type as ReachabilityType,
      publiclyAvailable: result.publiclyAvailable,
      callNotificationEmailFormat: result.callNotificationEmailFormat,
    }).pipe(
      // create() maps reachabilityId → id (EB-4148); guard against a missing id so
      // we never assign an empty `{ reachability: {} }` and get a cryptic BE error.
      switchMap((created: Reachability) => created.id
        ? of(created.id)
        : throwError(() => new Error(REACHABILITY_NO_ID_ERROR))),
    );
  }

  /** Whether we have enough context to offer the "Create new" tab. */
  private canCreateReachability(): boolean {
    return this.firstPhoneServiceIdForUa() > 0;
  }

  /** Pick the first PS owned by the active UA — used as the create scope. */
  private firstPhoneServiceIdForUa(): number {
    const ps = this.session.phoneServices().find(p => p.userAccountId === this.userAccountId);
    return ps?.id ?? 0;
  }

  deleteEmailNotif(email: EmailAddress, notif: NotificationData): void {
    this.api.deleteEmailNotification(email.domain, email.username, notif.notificationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const key = this.emailKey(email);
          this.emailNotifications.update(m => ({
            ...m,
            [key]: (m[key] ?? []).filter(n => n.notificationId !== notif.notificationId),
          }));
          this.toast.open('Notification removed', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.toast.open(extractErrorMessage(err, 'Failed to remove notification'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
  }

  // --- Fax CRUD ---

  /**
   * Book a new fax number — old CMT fax.dir.js `newFaxNumberDialog` → shared number dialog in
   * fax mode → `POST /faxnumbers`. Reloads the list so the new number arrives with its status,
   * and the customer so UA sender dropdowns see it. The user account is captured when the dialog
   * opens, so a route change while it is open cannot redirect the booking; the response only
   * touches this view while it still shows that user account (the component is reused across
   * user accounts — a late reload would list A's faxes on B's page).
   * @ticket EB-3809
   */
  openBookFaxDialog(): void {
    const brandId = this.session.brandId();
    if (!brandId || this.faxSaving()) return;
    const userAccountId = this.userAccountId;

    const ref = this.dialog.open(BookNumberDialogComponent, {
      data: { brandId, mode: 'fax' },
      disableClose: true,
    });
    ref.afterClosed().subscribe((result: BookNumberDialogResult | undefined) => {
      if (!result) return;
      this.faxBookingsInFlight.add(userAccountId);
      this.faxSaving.set(true);
      const settle = (): boolean => {
        this.faxBookingsInFlight.delete(userAccountId);
        const stillInView = userAccountId === this.userAccountId;
        if (stillInView) this.faxSaving.set(false);
        return stillInView;
      };
      this.api.bookFaxNumber(userAccountId, this.auth.currentUserId, toFaxBookingRequest(result))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            /** @ticket EB-3798 — usage: a fax number was booked (billable, regulation data involved). */
            this.loggingService.usage('action.invoke', { action: 'fax_book', outcome: 'success' });
            this.session.reloadCustomer();
            // Wording follows the callcenter booking toast (cc-numbers.component.ts).
            this.toast.open('Fax number booked successfully', 'OK', { duration: 3000 });
            if (settle()) this.reloadFaxNumbers(userAccountId);
          },
          error: (err) => {
            settle();
            this.toast.open(extractErrorMessage(err, 'Failed to book fax number'), 'OK', SNACKBAR_ERROR_CONFIG);
          },
        });
    });
  }

  private reloadFaxNumbers(userAccountId: number): void {
    this.api.getFaxNumbers(userAccountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          if (userAccountId !== this.userAccountId) return; // view moved on to another user account
          const known = new Set(this.faxNumbers().map(f => this.faxKey(f)));
          this.faxNumbers.set(list);
          list.filter(f => !known.has(this.faxKey(f))).forEach(f => this.loadFaxNotifications(f));
        },
        error: (err) => this.toast.open(
          extractErrorMessage(err, 'Fax number booked, but the list could not be reloaded — refresh the page'),
          'OK', SNACKBAR_ERROR_CONFIG),
      });
  }

  confirmDeleteFax(fax: FaxNumber): void {
    const number = this.faxKey(fax);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Fax Number',
        message: `Really delete fax number ${number}?`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'delete',
      },
    });

    dialogRef.afterClosed().pipe(
      filter((confirmed): confirmed is true => !!confirmed),
      switchMap(() => this.auth.user$.pipe(take(1))),
      switchMap(user => this.api.deleteFaxNumber(number, user?.userId ?? 0)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.faxNumbers.update(list => list.filter(f => this.faxKey(f) !== number));
        this.toast.open('Fax number deleted', 'OK', { duration: 3000 });
      },
      error: (err) => {
        this.toast.open(extractErrorMessage(err, 'Failed to delete fax number'), 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }

  // --- Fax Notifications ---

  loadFaxNotifications(fax: FaxNumber): void {
    const key = this.faxKey(fax);
    this.faxNotifLoading.update(m => ({ ...m, [key]: true }));

    forkJoin({
      notifications: this.api.getFaxNotifications(key),
      addable: this.api.getFaxAddableReachabilities(key),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.faxNotifications.update(m => ({ ...m, [key]: data.notifications }));
        this.faxAddableReachabilities.update(m => ({ ...m, [key]: data.addable }));
        this.faxNotifLoading.update(m => ({ ...m, [key]: false }));
      },
      error: () => {
        this.faxNotifLoading.update(m => ({ ...m, [key]: false }));
      },
    });
  }

  getFaxNotifs(fax: FaxNumber): NotificationData[] {
    return this.faxNotifications()[this.faxKey(fax)] ?? [];
  }

  getFaxAddable(fax: FaxNumber): ReachabilityRef[] {
    return this.faxAddableReachabilities()[this.faxKey(fax)] ?? [];
  }

  isFaxNotifLoading(fax: FaxNumber): boolean {
    return this.faxNotifLoading()[this.faxKey(fax)] ?? false;
  }

  /** Same dialog flow as email-notification add — see openAddEmailNotifDialog. */
  openAddFaxNotifDialog(fax: FaxNumber): void {
    const key = this.faxKey(fax);
    const ref = this.dialog.open(AddReachabilityDialogComponent, {
      data: {
        addableList: this.getFaxAddable(fax),
        sectionLabel: `Notification for fax ${this.faxDisplayNumber(fax)}`,
        allowCreate: this.canCreateReachability(),
        allowedTypes: ['FAX', 'SMS'],
      },
      disableClose: true,
    });

    ref.afterClosed().subscribe((result: AddReachabilityDialogResult | null) => {
      if (!result) return;
      const attach$ = this.resolveReachabilityId(result).pipe(
        switchMap(reachId => this.api.addFaxNotification(key, {
          reachability: { reachabilityId: reachId },
        })),
        takeUntilDestroyed(this.destroyRef),
      );
      attach$.subscribe({
        next: () => {
          this.loadFaxNotifications(fax);
          this.toast.open(
            result.mode === 'create' ? 'Reachability created and notification added' : 'Notification added',
            'OK',
            { duration: 3000 },
          );
        },
        error: (err) => {
          this.toast.open(extractErrorMessage(err, 'Failed to add notification'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
    });
  }

  deleteFaxNotif(fax: FaxNumber, notif: NotificationData): void {
    const key = this.faxKey(fax);
    this.api.deleteFaxNotification(key, notif.notificationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.faxNotifications.update(m => ({
            ...m,
            [key]: (m[key] ?? []).filter(n => n.notificationId !== notif.notificationId),
          }));
          this.toast.open('Notification removed', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.toast.open(extractErrorMessage(err, 'Failed to remove notification'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
  }

  faxDisplayNumber(fax: FaxNumber): string {
    return `${fax.diallingCode} ${fax.prefix} ${fax.suffix}`;
  }

  faxStatusText(fax: FaxNumber): string {
    return fax.status?.show || fax.status?.state || '';
  }
}
